import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { TicketReel } from "./TicketReel";
import { TicketWall } from "./TicketWall";
import { Confetti } from "../fx/Confetti";
import { Particles, RayBurst } from "../fx/Particles";
import { ProductImage } from "../ProductCard";
import { Button } from "../ui";
import { sfx } from "../../lib/sfx";
import { EASE } from "../../lib/motion";
import { useBodyLock } from "../../hooks/useBodyLock";
import { usePrefersReducedMotion } from "../../hooks/useBreakpoint";

export type DrawFx =
  | { kind: "single"; winnerName: string; winnerTicketNo: number; ticketCount?: number; ticketNos?: number[] }
  | {
      kind: "lobby";
      itemId: string;
      productName: string;
      ticketCount: number;
      ticketNos?: number[];
      winners: { name: string; ticketNo: number; orderId?: string }[];
      noEntries?: boolean;
    };

type Phase = "shuffle" | "spin" | "winner";

const SHUFFLE_MS = 1500;

/**
 * The raffle draw, staged as a ceremony: the ticket pool shuffles, a reel of
 * real ticket numbers spins down to the winning one, then the winner is
 * spotlit with confetti.
 */
export function RaffleDrawCeremony({
  fx,
  ticketNos,
  myTicketNos = [],
  prizeName,
  prizeImage,
  prizeImageBackgroundColor,
  isWinner = false,
  paymentHref,
  paymentPending = false,
  onClose,
}: {
  fx: DrawFx | null;
  ticketNos: number[];
  myTicketNos?: number[];
  prizeName?: string | null;
  prizeImage?: string | null;
  prizeImageBackgroundColor?: string | null;
  isWinner?: boolean;
  paymentHref?: string | null;
  paymentPending?: boolean;
  onClose: () => void;
}) {
  const reduced = usePrefersReducedMotion();
  const [phase, setPhase] = useState<Phase>("shuffle");
  useBodyLock(!!fx);

  const winningTicket =
    fx?.kind === "single" ? fx.winnerTicketNo : fx?.kind === "lobby" ? fx.winners[0]?.ticketNo ?? 1 : 1;
  const ceremonyTicketNos = fx?.ticketNos?.length ? fx.ticketNos : ticketNos;
  const ticketCount = ceremonyTicketNos.length;
  const isNoEntries = fx?.kind === "lobby" && (fx.noEntries || (fx.ticketCount === 0 && fx.winners.length === 0));
  const showPayment = isWinner && paymentPending && !!paymentHref;

  useEffect(() => {
    if (!fx) return;
    if (isNoEntries) {
      setPhase("winner");
      return;
    }
    setPhase("shuffle");
    const t = window.setTimeout(() => setPhase("spin"), reduced ? 200 : SHUFFLE_MS);
    return () => window.clearTimeout(t);
  }, [fx, reduced, isNoEntries]);

  useEffect(() => {
    if (phase === "winner" && !isNoEntries) sfx.win();
  }, [phase, isNoEntries]);

  useEffect(() => {
    if (!fx || !isNoEntries) return;
    const t = window.setTimeout(onClose, reduced ? 1500 : 3500);
    return () => window.clearTimeout(t);
  }, [fx, isNoEntries, onClose, reduced]);

  useEffect(() => {
    if (!fx) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && phase === "winner") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fx, phase, onClose]);

  return (
    <AnimatePresence>
      {fx && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label="Raffle draw"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="bg-noise fixed inset-0 z-[90] flex flex-col items-center justify-center overflow-y-auto bg-brand-dark/98 bg-mesh-deep px-5 py-10"
        >
          <Particles count={20} />
          {phase === "winner" && !isNoEntries && <Confetti fireKey="raffle-winner" intensity={3} />}

          {phase === "winner" && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="fixed right-4 top-4 z-20 rounded-full bg-white/5 p-2.5 text-white/60 backdrop-blur transition-colors hover:bg-white/10 hover:text-white"
            >
              <X size={18} />
            </button>
          )}

          <div
            className={`relative z-10 flex w-full flex-col items-center ${
              fx.kind === "lobby" && fx.winners.length > 6 ? "max-w-4xl" : "max-w-lg"
            }`}
          >
            <AnimatePresence mode="wait">
              {phase === "shuffle" && (
                <motion.div
                  key="shuffle"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  className="flex w-full flex-col items-center"
                >
                  <motion.p
                    className="font-display text-sm font-bold uppercase tracking-[0.3em] text-brand-pink"
                    animate={reduced ? {} : { opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 1.1, repeat: Infinity }}
                  >
                    Shuffling tickets
                  </motion.p>
                  <p className="mt-2 text-[11px] text-white/40">{ticketCount} in the drum</p>
                  <TicketWall
                    ticketNos={ceremonyTicketNos}
                    myTicketNos={myTicketNos}
                    shuffling
                    className="mt-7 w-full"
                  />
                </motion.div>
              )}

              {phase === "spin" && (
                <motion.div
                  key="spin"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.35, ease: EASE.outExpo }}
                  className="flex w-full flex-col items-center"
                >
                  <p className="mb-5 font-display text-sm font-bold uppercase tracking-[0.3em] text-brand-pink">
                    Drawing the winner
                  </p>
                  <TicketReel
                    key={
                      fx.kind === "lobby"
                        ? `${fx.itemId}-${fx.winners[0]?.ticketNo ?? 0}`
                        : `single-${fx.winnerTicketNo}`
                    }
                    ticketNos={ceremonyTicketNos}
                    winnerTicketNo={winningTicket}
                    onLanded={() => window.setTimeout(() => setPhase("winner"), reduced ? 0 : 550)}
                  />
                  {prizeName && <p className="mt-5 text-[11px] text-white/40">for {prizeName}</p>}
                </motion.div>
              )}

              {phase === "winner" && (
                <motion.div
                  key="winner"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.55, ease: EASE.outExpo }}
                  className="relative isolate flex w-full flex-col items-center text-center"
                >
                  <RayBurst active intensity={isNoEntries ? 1 : 3} />

                  {prizeImage && (
                    <motion.div
                      initial={{ opacity: 0, y: 20, rotate: -6 }}
                      animate={{ opacity: 1, y: 0, rotate: 0 }}
                      transition={{ delay: 0.1, duration: 0.5, ease: EASE.outExpo }}
                      className="mb-5 w-[min(100%,11rem)] overflow-hidden rounded-2xl border-2 border-amber-300/60 glow-brand-lg"
                    >
                      <ProductImage
                        src={prizeImage}
                        alt=""
                        backgroundColor={prizeImageBackgroundColor}
                        className="aspect-[5/7] w-full"
                      />
                    </motion.div>
                  )}

                  {fx.kind === "single" ? (
                    <>
                      <p className="mt-4 font-display text-xs font-bold uppercase tracking-[0.35em] text-gradient-holo">
                        {isWinner ? "You won" : "Winner"}
                      </p>
                      <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.25 }}
                        className="mt-2 font-display text-2xl font-extrabold lg:text-4xl"
                      >
                        {fx.winnerName}
                      </motion.p>
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        className="mt-1 font-display text-sm text-brand-pink"
                      >
                        Ticket #{fx.winnerTicketNo}
                      </motion.p>
                    </>
                  ) : isNoEntries ? (
                    <>
                      <p className="mt-4 font-display text-lg font-bold text-gradient">{fx.productName}</p>
                      <p className="mt-3 text-sm text-white/60">No tickets entered for this prize</p>
                      <p className="mt-1 text-[11px] text-white/40">Moving on to the next draw…</p>
                    </>
                  ) : (
                    <>
                      <p className="mt-4 font-display text-lg font-bold text-gradient">{fx.productName}</p>
                      <p className="mt-1 text-[11px] text-white/45">
                        {fx.winners.length} winner{fx.winners.length === 1 ? "" : "s"}
                      </p>
                      <div className="mt-3 max-h-[min(52vh,22rem)] w-full overflow-y-auto rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                        <ul className="grid grid-cols-3 gap-2 lg:grid-cols-5">
                          {fx.winners.map((w, i) => (
                            <motion.li
                              key={`${w.ticketNo}-${i}`}
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: Math.min(0.15 + i * 0.03, 1.2) }}
                              className="rounded-xl bg-white/5 px-2 py-2 text-center text-xs"
                            >
                              <span className="block truncate font-semibold leading-tight">{w.name}</span>
                              <span className="mt-0.5 block font-display tabular-nums text-brand-pink">#{w.ticketNo}</span>
                            </motion.li>
                          ))}
                        </ul>
                      </div>
                    </>
                  )}

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="mt-7 flex w-full max-w-xs flex-col gap-2"
                  >
                    {showPayment && (
                      <Link to={paymentHref!} className="block w-full">
                        <Button className="w-full py-3">Proceed to payment</Button>
                      </Link>
                    )}
                    <Button type="button" variant="outline" className="w-full py-3" onClick={onClose}>
                      Return to lobby
                    </Button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
